import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { auxCodesApi, clientsApi } from '../../../api/client';
import Modal, { Button, Badge } from '../../ui/Modal';
import { Label, Input, ErrorBanner } from '../../../auth/AuthShell';

/**
 * Admin > AUX Codes (2026-08-10)
 *
 * Per-tenant AUX (Not-Ready reason) codes shown in the softphone. Admins pick
 * a tenant (or the global/default set) and manage its codes. Agents see the
 * codes for the tenant they're assigned to (users.client_id), falling back to
 * the global set when their tenant defines none.
 *
 *  - "Global (default)" scope = client_id NULL. Fallback for every tenant.
 *  - Each tenant scope = that client's own codes (override the global set).
 *  - `code` is the machine value (lowercased/underscored); `label` is shown.
 */
const GLOBAL = 'global';

export default function AuxCodesPage() {
  const qc = useQueryClient();
  const [scope, setScope] = useState(GLOBAL);   // 'global' | <clientId>
  const [editing, setEditing] = useState(null); // row being edited/created

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: clientsApi.list,
  });
  const { data: codes = [], isLoading } = useQuery({
    queryKey: ['aux-codes', scope],
    queryFn: () => auxCodesApi.list(scope),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['aux-codes'] });
  const delMut = useMutation({ mutationFn: auxCodesApi.remove, onSuccess: invalidate });

  const scopeClientId = scope === GLOBAL ? null : scope;
  const scopeLabel = scope === GLOBAL
    ? 'Global (default)'
    : (clients.find((c) => c.id === scope)?.name || 'Tenant');

  const onDelete = (row) => {
    if (!confirm(`Delete AUX code "${row.label}" (${row.code})? This cannot be undone.`)) return;
    delMut.mutate(row.id);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">AUX Codes</h1>
          <p className="text-sm text-surface-muted dark:text-surface-dark-muted mt-1">
            Not-Ready / AUX reasons shown in the softphone, per tenant. Agents see their
            tenant's codes; tenants with none fall back to the Global set.
          </p>
        </div>
        <Button onClick={() => setEditing({ client_id: scopeClientId, code: '', label: '', active: true, sort_order: (codes.length) * 10 })}>
          + New AUX Code
        </Button>
      </div>

      {/* Scope picker */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Label className="!mb-0">Tenant:</Label>
        <select
          className="border rounded px-2 py-1 text-sm bg-white dark:bg-surface-dark"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
        >
          <option value={GLOBAL}>Global (default)</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <span className="text-xs text-surface-muted">
          Showing <strong>{scopeLabel}</strong> codes
        </span>
      </div>

      {isLoading ? (
        <div className="text-sm text-surface-muted">Loading…</div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-subtle dark:bg-surface-dark-subtle">
              <tr className="text-left">
                <th className="px-3 py-2 w-16">Order</th>
                <th className="px-3 py-2">Label (shown to agent)</th>
                <th className="px-3 py-2">Code (machine value)</th>
                <th className="px-3 py-2 w-24">Status</th>
                <th className="px-3 py-2 w-32 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-surface-muted">
                  No AUX codes for {scopeLabel}.
                  {scope !== GLOBAL && ' Agents in this tenant will use the Global set.'}
                </td></tr>
              )}
              {codes.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="px-3 py-2 text-surface-muted">{row.sort_order}</td>
                  <td className="px-3 py-2 font-medium">{row.label}</td>
                  <td className="px-3 py-2"><code className="text-xs">{row.code}</code></td>
                  <td className="px-3 py-2">
                    {row.active
                      ? <Badge tone="green">Active</Badge>
                      : <Badge tone="neutral">Hidden</Badge>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button className="text-brand hover:underline mr-3" onClick={() => setEditing(row)}>Edit</button>
                    <button className="text-red-600 hover:underline" onClick={() => onDelete(row)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <AuxCodeModal
          row={editing}
          scopeClientId={scopeClientId}
          scopeLabel={scopeLabel}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); invalidate(); }}
        />
      )}
    </div>
  );
}

function AuxCodeModal({ row, scopeClientId, scopeLabel, onClose, onSaved }) {
  const isNew = !row.id;
  const [label, setLabel] = useState(row.label || '');
  const [code, setCode] = useState(row.code || '');
  const [active, setActive] = useState(row.active !== false);
  const [sortOrder, setSortOrder] = useState(row.sort_order ?? 0);
  const [err, setErr] = useState(null);
  const [codeTouched, setCodeTouched] = useState(!isNew);

  // Auto-derive machine code from the label while creating (until the user
  // edits the code field directly).
  const deriveCode = (s) => s.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/g, '');

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) {
        return auxCodesApi.create({
          client_id: scopeClientId, code: code || deriveCode(label),
          label, active, sort_order: Number(sortOrder) || 0,
        });
      }
      return auxCodesApi.update(row.id, {
        code, label, active, sort_order: Number(sortOrder) || 0,
      });
    },
    onSuccess: onSaved,
    onError: (e) => setErr(e?.message || 'Save failed'),
  });

  return (
    <Modal
      open
      size="sm"
      title={isNew ? `New AUX Code — ${scopeLabel}` : `Edit AUX Code — ${scopeLabel}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => { setErr(null); save.mutate(); }} disabled={!label.trim() || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      {err && <ErrorBanner>{err}</ErrorBanner>}
      <div className="space-y-3">
        <div>
          <Label>Label (shown to agent)</Label>
          <Input
            value={label}
            autoFocus
            placeholder="e.g. Lunch"
            onChange={(e) => {
              setLabel(e.target.value);
              if (isNew && !codeTouched) setCode(deriveCode(e.target.value));
            }}
          />
        </div>
        <div>
          <Label>Code (machine value)</Label>
          <Input
            value={code}
            placeholder="e.g. lunch"
            onChange={(e) => { setCodeTouched(true); setCode(deriveCode(e.target.value)); }}
          />
          <p className="text-xs text-surface-muted mt-1">
            Lowercase, no spaces. Sent to reporting as the reason value. Keep it stable once agents use it.
          </p>
        </div>
        <div className="flex gap-4">
          <div className="w-28">
            <Label>Sort order</Label>
            <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
          </div>
          <label className="flex items-center gap-2 mt-6 text-sm">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Active (visible to agents)
          </label>
        </div>
      </div>
    </Modal>
  );
}
